/* The ledger. Every catalogued dataset, and the operational surface the rest of the page feeds.
 *
 * Written as a table rather than assembled from `DataTable` for one reason: sortable headers. That
 * component takes a column's label as a string and cannot hold a button, and sorting is what makes
 * a five-hundred-row listing usable. Its two other gifts are kept by hand — `data-label` on every
 * cell, which is what turns a row into a labelled card below 720px, and a `<caption>`, without
 * which a screen reader announces "table" and nothing else.
 *
 * **Unscored rows sort first in every order**, including a descending sort by score. A dataset
 * nobody has measured is a coverage gap rather than the bottom of the range, and burying it is how
 * an estate with a third of it unmeasured reads as fully measured.
 *
 * Long names are the normal case here — `COMMERCE_RETAIL.RAW_WAREHOUSE_INVENTORY` is 38 characters
 * — so the cell truncates the *object* and keeps the schema on its own line, and the full qualified
 * name is on the button's `title` and in its accessible name.
 */

import { useCallback } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Score } from '../api/scoring';
import type { SortKey } from '../model/rows';
import type { DatasetRow, DrawerTab, OptionalColumn } from '../model/types';

import {
  CoverageCell,
  EvaluatedCell,
  MovementCell,
  OwnerCell,
  ScoreBand,
  ScoreFigure,
  TargetCell,
} from './cells';
import { DimensionStrip } from './DimensionSummary';
import { STATUS_TONE } from './tone';

export interface DatasetTableProps {
  readonly rows: readonly DatasetRow[];
  readonly columns: ReadonlySet<OptionalColumn>;
  readonly sort: SortKey;
  readonly direction: 1 | -1;
  readonly onSort: (key: SortKey) => void;
  readonly selected: string | null;
  readonly onOpen: (datasetId: string, tab: DrawerTab) => void;
  readonly movementOn: boolean;
  readonly movementLoading: boolean;
  readonly scores: ReadonlyMap<string, Score>;
  readonly dimensionsLoading: boolean;
  readonly windowDays: number;
}

interface HeaderProps {
  readonly id: SortKey;
  readonly label: string;
  readonly numeric?: boolean | undefined;
  readonly sort: SortKey;
  readonly direction: 1 | -1;
  readonly onSort: (key: SortKey) => void;
  readonly title?: string | undefined;
}

function SortHeader({
  id,
  label,
  numeric,
  sort,
  direction,
  onSort,
  title,
}: HeaderProps): ReactElement {
  const active = sort === id;
  return (
    <th
      scope="col"
      className={numeric === true ? 'lq-table__num' : undefined}
      aria-sort={active ? (direction === 1 ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="lq-sortbtn"
        title={title}
        onClick={() => {
          onSort(id);
        }}
      >
        {label}
        <span className="lq-sortbtn__mark" aria-hidden="true">
          {active ? (direction === 1 ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

export function DatasetTable({
  rows,
  columns,
  sort,
  direction,
  onSort,
  selected,
  onOpen,
  movementOn,
  movementLoading,
  scores,
  dimensionsLoading,
  windowDays,
}: DatasetTableProps): ReactElement {
  const { t } = useTranslation();

  /* Arrow keys move between rows without leaving the keyboard, which is what makes a long ledger
     workable. Focus is moved rather than a "selected index" tracked in state: the browser's own
     focus is the single source of truth, so it cannot drift from what the reader sees. */
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLTableSectionElement>) => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step === 0) return;
    const buttons = [
      ...event.currentTarget.querySelectorAll<HTMLButtonElement>('button[data-rowpick]'),
    ];
    const here = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (here < 0) return;
    event.preventDefault();
    buttons[Math.max(0, Math.min(buttons.length - 1, here + step))]?.focus();
  }, []);

  return (
    <div className="lq-table-scroll lq-table-scroll--framed">
      <table className="lq-table lq-table--ledger lq-scores__table">
        <caption className="lq-visually-hidden">{t('scoring.ledger.caption')}</caption>
        <thead>
          <tr>
            <SortHeader
              id="name"
              label={t('scoring.listing.column.dataset')}
              sort={sort}
              direction={direction}
              onSort={onSort}
            />
            <SortHeader
              id="score"
              label={t('scoring.listing.column.score')}
              numeric
              sort={sort}
              direction={direction}
              onSort={onSort}
              title={t('scoring.column.scoreHint')}
            />
            <th scope="col" className="lq-scores__bandcol">
              <span className="lq-visually-hidden">
                {t('scoring.listing.column.band')}
              </span>
            </th>
            <SortHeader
              id="delta"
              label={t('scoring.column.change', { days: windowDays })}
              numeric
              sort={sort}
              direction={direction}
              onSort={onSort}
              title={t('scoring.column.changeHint')}
            />
            {columns.has('coverage') && (
              <SortHeader
                id="coverage"
                label={t('scoring.listing.column.coverage')}
                numeric
                sort={sort}
                direction={direction}
                onSort={onSort}
                title={t('scoring.column.coverageHint')}
              />
            )}
            {columns.has('dimensions') && (
              <th scope="col" title={t('scoring.column.dimensionsHint')}>
                {t('scoring.column.dimensions')}
              </th>
            )}
            <th scope="col">{t('scoring.column.status')}</th>
            {columns.has('target') && (
              <SortHeader
                id="target"
                label={t('scoring.column.target')}
                sort={sort}
                direction={direction}
                onSort={onSort}
                title={t('scoring.column.targetHint')}
              />
            )}
            {columns.has('evaluated') && (
              <SortHeader
                id="evaluated"
                label={t('scoring.column.evaluated')}
                sort={sort}
                direction={direction}
                onSort={onSort}
              />
            )}
            {columns.has('owner') && (
              <SortHeader
                id="owner"
                label={t('scoring.column.owner')}
                sort={sort}
                direction={direction}
                onSort={onSort}
                title={t('scoring.column.ownerHint')}
              />
            )}
            <th scope="col" className="lq-table__actions">
              <span className="lq-visually-hidden">{t('scoring.column.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody onKeyDown={onKeyDown}>
          {rows.map((row) => {
            const falling =
              row.movement?.delta.kind === 'down' && row.movement.delta.magnitude >= 1;
            return (
              <tr
                key={row.id}
                className={
                  row.id === selected
                    ? `lq-row--chosen lq-scores__row lq-scores__row--${row.status}`
                    : `lq-scores__row lq-scores__row--${row.status}`
                }
              >
                <td data-label={t('scoring.listing.column.dataset')}>
                  <button
                    type="button"
                    data-rowpick=""
                    className="lq-scores__pick"
                    aria-pressed={row.id === selected}
                    title={row.name}
                    /* The **qualified** name, explicitly. Without it the accessible name is the
                       two spans concatenated in visual order — "RAW_CUSTOMER_ORDERS
                       COMMERCE_RETAIL" — which is the name backwards. The eye reads the object
                       first and a screen reader should not have to. */
                    aria-label={row.name}
                    onClick={() => {
                      onOpen(row.id, 'decomposition');
                    }}
                  >
                    <span className="lq-scores__object lq-mono">{row.object}</span>
                    <span className="lq-scores__schema">{row.schema}</span>
                  </button>
                </td>
                <td
                  className="lq-table__num"
                  data-label={t('scoring.listing.column.score')}
                >
                  <ScoreFigure row={row} />
                </td>
                <td data-label={t('scoring.listing.column.band')}>
                  <ScoreBand value={row.value} target={row.target} label={row.name} />
                </td>
                <td
                  className="lq-table__num"
                  data-label={t('scoring.column.change', { days: windowDays })}
                >
                  <MovementCell
                    movement={row.movement}
                    reading={movementOn && movementLoading}
                  />
                </td>
                {columns.has('coverage') && (
                  <td
                    className="lq-table__num"
                    data-label={t('scoring.listing.column.coverage')}
                  >
                    <CoverageCell row={row} />
                  </td>
                )}
                {columns.has('dimensions') && (
                  <td data-label={t('scoring.column.dimensions')}>
                    <DimensionStrip
                      score={scores.get(row.id)}
                      reading={dimensionsLoading}
                    />
                  </td>
                )}
                <td data-label={t('scoring.column.status')}>
                  <StatusBadge
                    shape="square"
                    tone={STATUS_TONE[row.status]}
                    label={t(`scoring.status.${row.status}`)}
                    title={t(`scoring.status.${row.status}Hint`)}
                  />
                  {!row.evidenceComplete && (
                    <span
                      className="lq-scores__sub"
                      title={t('scoring.score.partialMeaning')}
                    >
                      {t('scoring.score.partial')}
                    </span>
                  )}
                </td>
                {columns.has('target') && (
                  <td data-label={t('scoring.column.target')}>
                    <TargetCell row={row} />
                  </td>
                )}
                {columns.has('evaluated') && (
                  <td data-label={t('scoring.column.evaluated')}>
                    <EvaluatedCell row={row} />
                  </td>
                )}
                {columns.has('owner') && (
                  <td data-label={t('scoring.column.owner')}>
                    <OwnerCell row={row} />
                  </td>
                )}
                <td
                  className="lq-table__actions"
                  data-label={t('scoring.column.actions')}
                >
                  {/* One contextual action, and it appears only where it has something to explain.
                      A "Why?" on every row would be a column of buttons; on the rows that fell it
                      is the shortest path from a finding to its cause. */}
                  {falling ? (
                    <button
                      type="button"
                      className="lq-btn lq-btn--secondary lq-btn--compact"
                      onClick={() => {
                        onOpen(row.id, 'change');
                      }}
                    >
                      {t('scoring.action.why')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="lq-linkish"
                      onClick={() => {
                        onOpen(row.id, 'decomposition');
                      }}
                    >
                      {t('scoring.action.open')}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
